"use client";

import {
  Dispatch,
  Fragment,
  SetStateAction,
  useContext,
  useState,
} from "react";
import { SurveyStep } from "@/components/common/resource-survey/components/survey-form/components/survey-step/survey-step";

import { useImmer } from "use-immer";
import { SurveyFormContext } from "@/components/common/resource-survey/components/survey-form/survey-form-context";
import { clientPost } from "@/service/client-api/client-api";
import { toast } from "sonner";
import { ResourceContext } from "@/contexts/resource-context";
import { logEvent } from "@/service/logs/log";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronLeftIcon, ChevronRightIcon, LoaderIcon } from "lucide-react";
import { FormError } from "@/forms/form-error";

function createAnswersModel(steps: SurveyStepVM[]) {
  return steps.map(({ nodes }) =>
    nodes.map((question: SurveyNodeVM) => {
      const { type, payload, label } = question;

      const data = {
        type,
        label,
      };

      if (type === "text") {
        return {
          ...data,
          value: "",
        };
      }

      if (type === "radio") {
        return {
          ...data,
          value: "",
          customAnswer: null,
        };
      }

      if (type === "range") {
        return {
          ...data,
          value: null,
        };
      }

      if (type === "range-list") {
        const { list } = payload as SurveyNodePayloadRangeListVM;
        return {
          ...data,
          list: list.map(({ label }) => ({
            label,
            value: null,
          })),
        };
      }
    }),
  );
}

export const SurveyForm = ({
  steps,
  id,
  setSuccess,
  resourceType,
  resourceId,
}: {
  steps: SurveyStepVM[];
  id: number;
  setSuccess: Dispatch<SetStateAction<any>>;
  resourceType: string;
  resourceId: number;
}) => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState(null);
  const [stepsAnswers, setStepsAnswers] = useImmer(createAnswersModel(steps));
  const { logView } = useContext(ResourceContext);

  const onSubmit = async () => {
    const answers = stepsAnswers.reduce((list, step) => [...list, ...step], []);

    try {
      setLoading(true);

      await clientPost(
        "survey.feedback",
        { id },
        {
          answers,
        },
      );
      setLoading(false);
      setSuccess(true);

      logEvent("survey_finish", logView, {
        resourceType,
        resourceId,
        survey_id: id,
      });
    } catch (error: { error: string; details: { label: string } }) {
      setLoading(false);

      toast.error(error.error);

      stepsAnswers.forEach((step, index) => {
        step.forEach((question) => {
          if (question.label === error?.details?.label) {
            setCurrentStep(index);
            setError("Odpowiedz na wszystkie wymagane pytania");
          }
        });
      });
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);

    logEvent("survey_change_step", logView, {
      resourceType,
      resourceId,
      survey_step_from: currentStep,
      survey_step_to: currentStep - 1,
      survey_id: id,
    });
  };
  const nextStep = () => {
    setCurrentStep(currentStep + 1);

    logEvent("survey_change_step", logView, {
      resourceType,
      resourceId,
      survey_step_from: currentStep,
      survey_step_to: currentStep + 1,
      survey_id: id,
    });
  };

  return (
    <div>
      <div className={"text-muted-foreground mb-2 text-sm"}>
        {currentStep + 1} z {steps.length}
      </div>
      <div className={"flex gap-1.5"}>
        {new Array(steps.length).fill(steps.length).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-1 w-full rounded-full bg-zinc-300",
              index <= currentStep && "bg-remedium-blue",
            )}
          />
        ))}
      </div>
      <FormError error={error} />

      <div
        className={cn("default-scrollbar mt-6 overflow-y-auto pr-2", "md:pr-0")}
      >
        <SurveyFormContext.Provider
          value={{ stepsAnswers, setStepsAnswers, currentStep }}
        >
          {steps.map((step, index) =>
            currentStep === index ? (
              <SurveyStep key={index} step={step} />
            ) : (
              <Fragment key={index}></Fragment>
            ),
          )}
        </SurveyFormContext.Provider>
      </div>

      <div
        className={
          "bottom-0 mt-6 flex flex-row-reverse items-center justify-between"
        }
      >
        {currentStep < steps.length - 1 && (
          <Button size={"icon"} variant={"blue"} onClick={nextStep}>
            <ChevronRightIcon size={"16px"} />
          </Button>
        )}

        {currentStep === steps.length - 1 && (
          <Button
            className={"w-[140px]"}
            variant={"blue"}
            onClick={onSubmit}
            disabled={loading}
          >
            {loading && <LoaderIcon className="animate-spin" />}
            Zakończ
          </Button>
        )}

        {currentStep > 0 && (
          <Button size={"icon"} variant={"ghost"} onClick={prevStep}>
            <ChevronLeftIcon size={"16px"} />
          </Button>
        )}
      </div>
    </div>
  );
};
